import { BalanceOfAbi, Erc4626ReadAbi, TotalSupplyAbi } from '../abis'
import type { SavingsRegistryEntry } from '../registry'
import { ONE_E18, toBigInt, type SavingsReader } from './shared'

/**
 * ERC-4626 whose exit is capped by a live inventory — four calls:
 *   1. `totalAssets()`
 *   2. `totalSupply()`
 *   3. `convertToAssets(10**shareDecimals)`
 *   4. `underlying.balanceOf(inventoryContract ?? vault)`
 *
 * Identical to [the generic 4626 reader](./erc4626.ts) for price and
 * size; the fourth call is what makes `liquidity` truthful for vaults
 * that do **not** custody everything they owe. Pair it with
 * `withdrawalMode: 'instant-capped'` (or `'fee-or-queued'` when the
 * protocol also charges for skipping the queue) so `fetchPublic` uses
 * the capacity instead of reporting `totalAssets` as withdrawable.
 *
 * ## Where the inventory lives
 *
 * - **Spark Vaults V2** (`spUSDC`/`spUSDT`/`spETH`/`spPYUSD`/`spUSDG`):
 *   the vault itself. `maxWithdraw`/`maxRedeem` clamp to
 *   `asset.balanceOf(address(this))`, everything else having been pulled
 *   out by `take()` into the Spark Liquidity Layer. Leave
 *   `inventoryContract` unset — it defaults to the share token.
 * - **Spark Savings V1** (`sUSDC`): the vault holds no USDC at all, it
 *   holds sUSDS and swaps through PSM3, so the cap is the PSM3
 *   **pocket**'s USDC balance. Pin that as `inventoryContract`.
 *
 * Both are free, same-block exits within the cap, so the reader reports
 * `withdrawFeeBps: 0` and `instantRedeemEnabled: true`. A failed
 * inventory read leaves the capacity unset, which `fetchPublic` renders
 * as `liquidity: 0` — conservative rather than optimistic, and it never
 * drops an otherwise-healthy entry.
 */
export const readerErc4626Idle = (
  entry: SavingsRegistryEntry,
): SavingsReader => {
  const shareUnit = 10n ** BigInt(entry.decimals)
  const underlyingUnit =
    10n ** BigInt(entry.underlyingDecimals ?? entry.decimals)
  const inventory = entry.inventoryContract ?? entry.address
  return {
    calls: [
      { address: entry.address, name: 'totalAssets', params: [] },
      { address: entry.address, name: 'totalSupply', params: [] },
      {
        address: entry.address,
        name: 'convertToAssets',
        params: [shareUnit],
      },
      { address: entry.underlying, name: 'balanceOf', params: [inventory] },
    ],
    abis: [Erc4626ReadAbi, TotalSupplyAbi, Erc4626ReadAbi, BalanceOfAbi],
    parse: ([assets, supply, rate, inventoryBalance]) => {
      const totalAssets = toBigInt(assets)
      const totalSupply = toBigInt(supply)
      const convertToAssetsRaw = toBigInt(rate)
      if (
        totalAssets === undefined ||
        totalSupply === undefined ||
        convertToAssetsRaw === undefined
      ) {
        return undefined
      }

      // Enrichment only — must not drop an otherwise-healthy entry.
      const capacity = toBigInt(inventoryBalance)

      return {
        totalAssets,
        totalSupply,
        exchangeRate: (convertToAssetsRaw * ONE_E18) / underlyingUnit,
        ...(capacity !== undefined
          ? {
              instantRedeemCapacity: capacity,
              instantRedeemEnabled: true,
              withdrawFeeBps: 0,
            }
          : {}),
        ...(entry.withdrawQueue ? { withdrawQueue: entry.withdrawQueue } : {}),
      }
    },
  }
}
